import MusicIcon from "@/assets/images/icons/music-icon.svg";
import PlayIcon from "@/assets/images/icons/play-icon.svg";
import { CourseSkillLevelBadge } from "@/components/ui/CourseSkillLevelBadge";
import { CaptionText, CardTitle } from "@/components/ui/Typography";
import type { PersonalHubItem } from "@/lib/derivePersonalHubModel";
import { useIsMobile } from "@/lib/useDeviceType";
import type { Course } from "@/types";
import { buildLessonUrl } from "@/utils/buildLessonUrl";
import { getLessonStructuredTitleMeta } from "@/utils/lessonStructuredTitle";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { Link } from "expo-router";
import React from "react";
import { Platform, Pressable } from "react-native";
import { SizableText, XStack, YStack } from "tamagui";
import { BRAND_COLORS, HUB_COPY, PERSONAL_HUB, useLandscapeThumbWidth } from "./personalHubTokens";

type Props = {
  item: PersonalHubItem;
  emphasis?: boolean;
  tile?: boolean;
  onOpenSurvey?: () => void;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function courseAvatarUri(course: Course, isMobile: boolean): string | null {
  return (
    (isMobile ? course.avatar_url_mobile?.trim() : course.avatar_url_desktop?.trim()) ||
    course.avatar_url_mobile?.trim() ||
    course.avatar_url_desktop?.trim() ||
    null
  );
}

function tileThumbSize(isMobile: boolean): number {
  return isMobile ? PERSONAL_HUB.thumbMobile : PERSONAL_HUB.thumbDense;
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: string }) {
  return (
    <SizableText fontSize={10} fontWeight="700" letterSpacing={1.1} textTransform="uppercase" color={BRAND_COLORS.accent}>
      {children}
    </SizableText>
  );
}

function ResumePill() {
  return (
    <XStack bg={BRAND_COLORS.accent} px="$2.5" py="$1.5" br={20} ai="center" gap={5} flexShrink={0}>
      <PlayIcon width={10} height={10} color="#FFFFFF" />
      <SizableText fontSize={12} fontWeight="700" color="#FFFFFF">
        {HUB_COPY.continue.cta}
      </SizableText>
    </XStack>
  );
}

function LandscapeThumb({ uri, width, progress }: { uri?: string | null; width: number; progress: number }) {
  const p = Math.min(100, Math.max(0, progress));
  return (
    <YStack w={width} aspectRatio={16 / 9} br={8} overflow="hidden" bg="#E8E4DF" flexShrink={0} position="relative">
      {uri ? (
        <Image source={{ uri }} style={{ width: "100%", height: "100%" }} contentFit="cover" recyclingKey={uri} />
      ) : null}
      <YStack position="absolute" bottom={0} left={0} right={0} h={3} bg="rgba(0,0,0,0.15)">
        <YStack h="100%" bg={BRAND_COLORS.accent} w={`${p}%`} />
      </YStack>
    </YStack>
  );
}

function RoundThumb({ uri, size, fallbackBg = "#E8E4DF" }: { uri?: string | null; size: number; fallbackBg?: string }) {
  return (
    <YStack w={size} h={size} br={size / 2} overflow="hidden" bg={fallbackBg} borderWidth={1} borderColor="rgba(0,0,0,0.06)" flexShrink={0} ai="center" jc="center">
      {uri ? (
        <Image source={{ uri }} style={{ width: size, height: size }} contentFit="cover" recyclingKey={uri} />
      ) : null}
    </YStack>
  );
}

function CircleIcon({ size, bg, borderColor, children }: { size: number; bg: string; borderColor?: string; children: React.ReactNode }) {
  return (
    <YStack w={size} h={size} br={size / 2} bg={bg} borderWidth={borderColor ? 1 : 0} borderColor={borderColor} ai="center" jc="center" flexShrink={0}>
      {children}
    </YStack>
  );
}

function TileText({ eyebrow, title, compact }: { eyebrow: string; title: string; compact?: boolean }) {
  return (
    <YStack f={1} miw={0} gap={2} jc="center">
      <Eyebrow>{eyebrow}</Eyebrow>
      <CardTitle fontWeight="600" color={BRAND_COLORS.text} numberOfLines={2}>
        {title}
      </CardTitle>
    </YStack>
  );
}

// ─── Shell wrappers ───────────────────────────────────────────────────────────

function TileShell({ children, href, onPress, accessibilityLabel }: { children: React.ReactNode; href?: string; onPress?: () => void; accessibilityLabel: string }) {
  const shell = (
    <XStack
      w="100%"
      f={1}
      h="100%"
      alignSelf="stretch"
      br={PERSONAL_HUB.tileRadius}
      bg={PERSONAL_HUB.tileBg}
      ai="center"
      p="$2.5"
      gap="$2.5"
      minHeight={PERSONAL_HUB.tileMinHeight}
      accessibilityLabel={accessibilityLabel}
      style={{ cursor: href || onPress ? "pointer" : undefined }}
      borderWidth={1}
      borderColor="transparent"
      hoverStyle={{ bg: "rgba(244, 241, 237, 0.9)", borderColor: "rgba(0,0,0,0.08)" }}
      pressStyle={{ opacity: 0.96, scale: 0.995 }}
      animation="quick"
      onPress={onPress}
    >
      {children}
    </XStack>
  );
  if (href) return <Link href={href as any} asChild>{shell}</Link>;
  return shell;
}

function EmphasisShell({ children, href, onPress, accessibilityLabel, isMobile }: { children: React.ReactNode; href?: string; onPress?: () => void; accessibilityLabel: string; isMobile: boolean }) {
  const shell = (
    <XStack
      w="100%"
      px={isMobile ? "$3" : "$3.5"}
      py={isMobile ? "$2.5" : "$3"}
      gap={isMobile ? "$2.5" : "$3"}
      ai="center"
      accessibilityLabel={accessibilityLabel}
      style={{ cursor: href || onPress ? "pointer" : undefined }}
      hoverStyle={{ opacity: 0.97 }}
      pressStyle={{ opacity: 0.94, scale: 0.998 }}
      animation="quick"
      onPress={onPress}
    >
      {children}
    </XStack>
  );
  if (href) return <Link href={href as any} asChild>{shell}</Link>;
  return shell;
}

// ─── Cards ────────────────────────────────────────────────────────────────────

function ContinueCard({
  item,
  emphasis,
  tile,
  isMobile,
}: {
  item: Extract<PersonalHubItem, { kind: "continue" }>;
  emphasis?: boolean;
  tile?: boolean;
  isMobile: boolean;
}) {
  const thumbWidth = useLandscapeThumbWidth();
  const { lesson } = item;
  const href = buildLessonUrl({ courseSlug: lesson.course_slug ?? lesson.slug, lessonSlug: lesson.slug });
  const { displayTitle, metaLine } = getLessonStructuredTitleMeta(lesson, { weekFormat: isMobile ? "short" : "long" });
  const subtitle = metaLine || (lesson.course_title && lesson.course_title !== displayTitle ? lesson.course_title : null);

  if (emphasis) {
    return (
      <EmphasisShell href={href} accessibilityLabel={`${HUB_COPY.continue.eyebrow}: ${displayTitle}`} isMobile={isMobile}>
        <LandscapeThumb uri={lesson.thumbnail} width={thumbWidth} progress={lesson.progressPercentage ?? 0} />
        <YStack f={1} miw={0} gap={2} jc="center">
          <Eyebrow>{HUB_COPY.continue.eyebrow}</Eyebrow>
          <CardTitle color={BRAND_COLORS.text} numberOfLines={2} letterSpacing={-0.2}>
            {displayTitle}
          </CardTitle>
          {subtitle ? (
            <CaptionText color={BRAND_COLORS.muted} numberOfLines={1}>
              {subtitle}
            </CaptionText>
          ) : null}
        </YStack>
        {!isMobile ? <ResumePill /> : null}
      </EmphasisShell>
    );
  }

  if (!tile) return null;
  return (
    <TileShell href={href} accessibilityLabel={`${HUB_COPY.continue.eyebrow}: ${displayTitle}`}>
      <RoundThumb uri={lesson.thumbnail} size={tileThumbSize(isMobile)} />
      <TileText eyebrow={HUB_COPY.continue.eyebrow} title={displayTitle} compact={isMobile} />
    </TileShell>
  );
}

function DashboardUpdateLink({
  onPress,
}: {
  onPress?: () => void;
  compact?: boolean;
}) {
  if (!onPress) return null;

  const handlePress = (e?: { stopPropagation?: () => void }) => {
    e?.stopPropagation?.();
    onPress();
  };

  return (
    <Pressable
      onPress={handlePress}
      onPressIn={(e) => e.stopPropagation()}
      hitSlop={8}
      {...(Platform.OS === "web"
        ? {
            onMouseDown: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          }
        : {})}
      accessibilityRole="button"
      accessibilityLabel={HUB_COPY.dashboard.updateCta}
      style={[
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        {
          pointerEvents: "auto" as const,
          width: 32,
          height: 32,
          alignItems: "center" as const,
          justifyContent: "center" as const,
        },
      ]}
    >
      <MaterialCommunityIcons
        name="cog-outline"
        size={20}
        color={BRAND_COLORS.muted}
      />
    </Pressable>
  );
}

function DashboardCard({
  item,
  emphasis,
  tile,
  isMobile,
  onOpenSurvey,
}: {
  item: Extract<PersonalHubItem, { kind: "dashboard" }>;
  emphasis?: boolean;
  tile?: boolean;
  isMobile: boolean;
  onOpenSurvey?: () => void;
}) {
  const { course, href } = item;
  const uri = courseAvatarUri(course, isMobile);
  const thumbSize = tileThumbSize(isMobile);

  if (emphasis) {
    return (
      <XStack
        w="100%"
        px={isMobile ? "$3" : "$3.5"}
        py={isMobile ? "$2.5" : "$3"}
        gap={isMobile ? "$2.5" : "$3"}
        ai="center"
      >
        <Link href={href as any} asChild>
          <XStack
            f={1}
            miw={0}
            gap={isMobile ? "$2.5" : "$3"}
            ai="center"
            accessibilityLabel={`${HUB_COPY.dashboard.eyebrow}: ${course.title}`}
            hoverStyle={{ opacity: 0.97 }}
            pressStyle={{ opacity: 0.94, scale: 0.998 }}
            animation="quick"
            cursor={Platform.OS === "web" ? "pointer" : undefined}
          >
            <RoundThumb uri={uri} size={thumbSize + 8} fallbackBg="#D4CFC4" />
            <YStack f={1} miw={0} gap={2} jc="center">
              <Eyebrow>{HUB_COPY.dashboard.eyebrow}</Eyebrow>
              <XStack ai="center" jc="flex-start" gap={10} miw={0}>
                <CardTitle
                  flexGrow={0}
                  flexShrink={1}
                  minWidth={0}
                  color={BRAND_COLORS.text}
                  letterSpacing={-0.2}
                >
                  {course.title}
                </CardTitle>
                {!isMobile && course.skill_level != null ? (
                  <CourseSkillLevelBadge level={course.skill_level} showLabel backgroundColor={BRAND_COLORS.pageBg} />
                ) : null}
              </XStack>
            </YStack>
          </XStack>
        </Link>
        {onOpenSurvey ? (
          <YStack alignSelf="stretch" jc="flex-end">
            <DashboardUpdateLink onPress={onOpenSurvey} />
          </YStack>
        ) : null}
      </XStack>
    );
  }

  if (!tile) return null;

  return (
    <XStack
      w="100%"
      f={1}
      alignSelf="stretch"
      br={PERSONAL_HUB.tileRadius}
      bg={PERSONAL_HUB.tileBg}
      ai="center"
      p="$2.5"
      gap="$2"
      minHeight={PERSONAL_HUB.tileMinHeight}
      borderWidth={1}
      borderColor="transparent"
    >
      <Link href={href as any} asChild>
        <XStack
          f={1}
          miw={0}
          gap="$2.5"
          ai="center"
          accessibilityLabel={`${HUB_COPY.dashboard.eyebrow}: ${course.title}`}
          hoverStyle={{ opacity: 0.97 }}
          pressStyle={{ opacity: 0.96, scale: 0.995 }}
          animation="quick"
          cursor={Platform.OS === "web" ? "pointer" : undefined}
        >
          <RoundThumb uri={uri} size={thumbSize} fallbackBg="#D4CFC4" />
          <YStack f={1} miw={0} gap={2} jc="center">
            <Eyebrow>{HUB_COPY.dashboard.eyebrow}</Eyebrow>
            <CardTitle fontWeight="600" color={BRAND_COLORS.text}>
              {course.title}
            </CardTitle>
          </YStack>
        </XStack>
      </Link>
      {onOpenSurvey ? (
        <YStack alignSelf="stretch" jc="flex-end">
          <DashboardUpdateLink onPress={onOpenSurvey} />
        </YStack>
      ) : null}
    </XStack>
  );
}

function SetupCard({ emphasis, tile, isMobile, onOpenSurvey }: { emphasis?: boolean; tile?: boolean; isMobile: boolean; onOpenSurvey?: () => void }) {
  const iconSize = tileThumbSize(isMobile) + (emphasis ? 8 : 0);
  const icon = (
    <CircleIcon size={iconSize} bg={PERSONAL_HUB.accentSoft} borderColor="rgba(225, 113, 0, 0.18)">
      <MusicIcon width={iconSize * 0.42} height={iconSize * 0.42} />
    </CircleIcon>
  );

  if (emphasis) {
    return (
      <EmphasisShell accessibilityLabel={HUB_COPY.setup.title} onPress={onOpenSurvey} isMobile={isMobile}>
        {icon}
        <YStack f={1} miw={0} gap={2} jc="center">
          <Eyebrow>{HUB_COPY.setup.eyebrow}</Eyebrow>
          <CardTitle color={BRAND_COLORS.text} numberOfLines={2} letterSpacing={-0.2}>
            {HUB_COPY.setup.title}
          </CardTitle>
          <CaptionText color={BRAND_COLORS.muted} numberOfLines={2}>
            {HUB_COPY.setup.subtitle}
          </CaptionText>
        </YStack>
      </EmphasisShell>
    );
  }

  if (!tile) return null;
  return (
    <TileShell accessibilityLabel={HUB_COPY.setup.title} onPress={onOpenSurvey}>
      {icon}
      <TileText eyebrow={HUB_COPY.setup.eyebrow} title={HUB_COPY.setup.title} compact={isMobile} />
    </TileShell>
  );
}

function PurchasedCard({ item, emphasis, tile, isMobile }: { item: Extract<PersonalHubItem, { kind: "purchased" }>; emphasis?: boolean; tile?: boolean; isMobile: boolean }) {
  const countLabel = HUB_COPY.purchased.countLabel(item.courseCount);
  const iconSize = tileThumbSize(isMobile) + (emphasis ? 8 : 0);
  const icon = (
    <CircleIcon size={iconSize} bg="rgba(36, 171, 226, 0.1)">
      <MaterialCommunityIcons name="bookshelf" size={iconSize * 0.46} color={BRAND_COLORS.logoBlue} />
    </CircleIcon>
  );

  if (emphasis) {
    return (
      <EmphasisShell href="/purchased-courses" accessibilityLabel={HUB_COPY.purchased.title} isMobile={isMobile}>
        {icon}
        <YStack f={1} miw={0} gap={2} jc="center">
          <Eyebrow>{HUB_COPY.purchased.eyebrow}</Eyebrow>
          <CardTitle color={BRAND_COLORS.text} numberOfLines={2} letterSpacing={-0.2}>
            {HUB_COPY.purchased.title}
          </CardTitle>
          <CaptionText color={BRAND_COLORS.muted} numberOfLines={1}>
            {countLabel}
          </CaptionText>
        </YStack>
      </EmphasisShell>
    );
  }

  if (!tile) return null;
  return (
    <TileShell href="/purchased-courses" accessibilityLabel={HUB_COPY.purchased.title}>
      {icon}
      <TileText eyebrow={HUB_COPY.purchased.eyebrow} title={countLabel} compact={isMobile} />
    </TileShell>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export const PersonalHubItemCard = React.memo(function PersonalHubItemCard({
  item,
  emphasis,
  tile,
  onOpenSurvey,
}: Props) {
  const isMobile = useIsMobile();
  switch (item.kind) {
    case "continue":
      return <ContinueCard item={item} emphasis={emphasis} tile={tile} isMobile={isMobile} />;
    case "dashboard":
      return <DashboardCard item={item} emphasis={emphasis} tile={tile} isMobile={isMobile} onOpenSurvey={onOpenSurvey} />;
    case "setup":
      return (
        <SetupCard
          emphasis={emphasis}
          tile={tile}
          isMobile={isMobile}
          onOpenSurvey={onOpenSurvey}
        />
      );
    case "purchased":
      return <PurchasedCard item={item} emphasis={emphasis} tile={tile} isMobile={isMobile} />;
  }
});
